import axios from 'axios';
import { useEffect, useState } from 'react';
import { UCP_PROXY_PATH_PREFIX } from 'src/modules/global-vars';
import { getAccessTokenFromStorage } from 'src/stores/Authentication';

// Derived from the shared prefix so it cannot drift from the interceptor's exclusion rule.
export const UCP_IMAGE_PROXY_PATTERN = `${UCP_PROXY_PATH_PREFIX}image?path=`;

export const isUcpImageUrl = (url: string | undefined): boolean => !!url?.includes(UCP_IMAGE_PROXY_PATTERN);

const useAuthenticatedImageSrc = (src: string | undefined, authenticationNeeded: boolean): string | undefined => {
  const [effectiveSrc, setEffectiveSrc] = useState<string | undefined>(authenticationNeeded ? undefined : src);

  useEffect(() => {
    if (!authenticationNeeded) {
      setEffectiveSrc(src);
      return;
    }
    if (!src) return;
    const token = getAccessTokenFromStorage();
    if (!token) {
      setEffectiveSrc(src);
      return;
    }
    const controller = new AbortController();
    let blobUrl: string | undefined;
    axios
      .get<Blob>(src, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
      .then((res) => {
        blobUrl = URL.createObjectURL(res.data);
        setEffectiveSrc(blobUrl);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setEffectiveSrc(src); // fallback to original URL → browser fires onError
      });
    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src, authenticationNeeded]);

  return effectiveSrc;
};

export default useAuthenticatedImageSrc;
